import { useCallback, useMemo, useState } from 'react';

import { useIntersection } from '@/shared/hooks/useIntersection/useIntersection';

import { type SelectionPanelProps } from '../SelectionPanel/SelectionPanel.types';
import { type ServerSelectProps } from '../types/server-selection.types';
import { type SelectionOption } from '../types/selection-option.types';
import { useSelectionFilter } from '../useSelectionFilter/useSelectionFilter';
import { useSelectionAnnouncer } from '../useSelectionAnnouncer/useSelectionAnnouncer';
import { useSelectionKeyboard } from '../useSelectionKeyboard/useSelectionKeyboard';
import {
  SERVER_SELECT_PREFETCH_MARGIN,
  useServerOptions,
} from '../useServerOptions/useServerOptions';

export type UseServerSelectLogicInput = Omit<
  ServerSelectProps,
  'id' | 'className' | 'size' | 'placeholder' | 'aria-describedby' | 'aria-labelledby'
>;

export interface UseServerSelectLogic {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  panel: Omit<SelectionPanelProps, 'mode'>;
  selectedOption: SelectionOption | null;
  isClearVisible: boolean;
  clear: () => void;
}

const EMPTY_PINNED: readonly string[] = [];

/**
 * The single-select server picker.
 *
 * A thin wrapper over `useServerOptions` plus the panel wiring — everything that
 * is genuinely about paging, debouncing and resolving lives there, so the multi
 * sibling shares it rather than reimplementing it.
 */
export function useServerSelectLogic({
  value,
  onValueChange,
  useOptionsQuery,
  useResolveIds,
  isReadOnly = false,
  isDisabled = false,
  isClearable = true,
  searchPlaceholder,
  emptyLabel,
  emptyContent,
  loadingContent,
  errorContent,
  renderOption,
}: UseServerSelectLogicInput): UseServerSelectLogic {
  const [isOpen, setIsOpen] = useState(false);
  const announcer = useSelectionAnnouncer();

  const pinnedValues = useMemo(() => (value === null ? EMPTY_PINNED : [value]), [value]);
  const server = useServerOptions({ pinnedValues, useOptionsQuery, useResolveIds });

  // The server already filtered; filtering the answer again would hide rows it
  // deliberately returned. This pass is only here for the render cap and the
  // section shape the panel expects.
  const filtered = useSelectionFilter({
    options: server.options,
    term: '',
    selected: useMemo(() => new Set(value === null ? [] : [value]), [value]),
  });

  // Entry only: Radix's RadioGroup already moves between the rows.
  const keyboard = useSelectionKeyboard({ options: filtered.visibleOptions, isEntryOnly: true });

  const sentinelRef = useIntersection(server.loadMore, {
    isEnabled: server.canLoadMore && !server.isFetching,
    rootMargin: SERVER_SELECT_PREFETCH_MARGIN,
  });

  const onSelect = useCallback(
    (candidate: string) => {
      // Announced, not merely refused — the shared announcer, so the single
      // control cannot say something different from its multi siblings.
      if (isReadOnly) {
        announcer.announceReadOnlyRefusal();
        return;
      }

      onValueChange(candidate);
      // A single choice is the whole gesture — staying open would leave the
      // operator to dismiss a list they are done with.
      setIsOpen(false);
    },
    [announcer, isReadOnly, onValueChange],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) server.setSearch('');
    },
    [server],
  );

  const isOptionDisabled = useCallback((option: SelectionOption) => option.isDisabled === true, []);

  const panel: Omit<SelectionPanelProps, 'mode'> = {
    filter: { term: server.search, onChange: server.setSearch, placeholder: searchPlaceholder },
    list: {
      sections: filtered.sections,
      selected: new Set(value === null ? [] : [value]),
      createdValues: filtered.createdValues,
      activeValue: null,
      isOptionDisabled,
      isReadOnly,
      onSelect,
      renderOption,
    },
    status: {
      isLoading: server.isFetching && server.options.length === 0,
      isError: server.isError,
      onRetry: server.retry,
      hiddenCount: filtered.hiddenCount,
      isEmpty: filtered.isEmpty,
      emptyLabel,
      emptyContent,
      loadingContent,
      errorContent,
    },
    loadMore: {
      onLoadMore: server.loadMore,
      canLoadMore: server.canLoadMore,
      isFetching: server.isFetching && server.options.length > 0,
    },
    announcement: announcer.announcement,
    singleValue: value,
    listRef: keyboard.listRef,
    sentinelRef,
    onKeyDown: keyboard.onKeyDown,
  };

  return {
    isOpen,
    onOpenChange,
    panel,
    selectedOption: value === null ? null : (server.byValue.get(value) ?? null),
    isClearVisible: isClearable && value !== null && !isDisabled && !isReadOnly,
    clear: useCallback(() => {
      // `null`, never `''`: an empty string is a value, and a filter that carries
      // one asks the api for rows whose id is the empty string.
      onValueChange(null);
    }, [onValueChange]),
  };
}
